<script lang="ts">
  // Directory — the merged Users list (everyone at Toyama). Admin also sees
  // two continuity columns (status dot + talk-to-them-by) sourced from the
  // continuity People register; that query only ever fires for admin
  // (`enabled: session.isAdmin`), so hr never even requests admin-only data.
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Plus } from 'lucide-svelte';
  import { hrUsersApi, type HRCreateUserBody, type HRUpdateUserBody } from '$lib/api/hr';
  import { hrDevicesApi } from '$lib/api/devices';
  import { peopleApi } from '$lib/api/people';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import StatusBadge from '$lib/components/ui/StatusBadge.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import RowActions from '$lib/components/ui/RowActions.svelte';
  import { relativeTime } from '$lib/utils/time';
  import { copyText, formatPersonDetails } from '$lib/utils/personDetails';
  import type { HRUser, OKRColour, PunchMode, Role } from '$lib/types';

  const PUNCH_MODES: { value: PunchMode; label: string; hint: string }[] = [
    { value: 'site', label: 'On site', hint: 'Must punch at Office, Hoskote, or another recognised site' },
    {
      value: 'field',
      label: 'Hotel field technician',
      hint: 'Site if inside one, otherwise they name the hotel'
    },
    { value: 'untagged', label: 'Sales', hint: 'On the clock, no location — customer visits are not tagged' },
    { value: 'home', label: 'Home or office', hint: 'Office if inside the geofence, otherwise Home' }
  ];
  const PUNCH_LABEL: Record<PunchMode, string> = {
    site: 'On site',
    field: 'Hotel field technician',
    untagged: 'Sales',
    home: 'Home or office'
  };

  function openDetail(u: HRUser) {
    route.push({ tab: 'directory', sub: 'person', userId: u.id });
  }

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['hr-users-mgmt'],
    queryFn: () => hrUsersApi.list()
  }));
  // Admin-only continuity data (status dot + talk-to-them-by) — never fired
  // for hr, per the Directory merge's privilege boundary.
  const continuityQuery = createQuery(() => ({
    queryKey: ['continuity-people'],
    queryFn: () => peopleApi.list(),
    enabled: session.isAdmin
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['hr-users-mgmt'] });
    queryClient.invalidateQueries({ queryKey: ['hr-users'] });
    queryClient.invalidateQueries({ queryKey: ['hr-devices'] });
  }

  let q = $state('');
  // Search first, then the active/inactive chips — so the chip counts
  // describe the list you are actually looking at, not the whole directory.
  const searchedUsers = $derived(
    (query.data ?? []).filter((u) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      return (u.name || '').toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
    })
  );

  type StatusFilter = 'all' | 'active' | 'inactive';
  let statusFilter = $state<StatusFilter>('all');
  const statusCounts = $derived({
    all: searchedUsers.length,
    active: searchedUsers.filter((u) => u.active).length,
    inactive: searchedUsers.filter((u) => !u.active).length
  });
  const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
    { id: 'all', label: 'Everyone' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' }
  ];
  const filteredUsers = $derived(
    searchedUsers.filter((u) => statusFilter === 'all' || u.active === (statusFilter === 'active'))
  );
  const isFiltered = $derived(statusFilter !== 'all' || q.trim() !== '');

  const LAMP_TONE: Record<OKRColour, 'bad' | 'warn' | 'good'> = { red: 'bad', amber: 'warn', green: 'good' };
  const continuityFor = $derived((userId: number) => (continuityQuery.data ?? []).find((p) => p.id === userId));

  // Role options offered to the caller: only admin may grant the admin role
  // (server enforces this too — see internal/api/handlers/hr_users.go).
  const roleOptions = $derived<Role[]>(session.isAdmin ? ['employee', 'hr', 'admin'] : ['employee', 'hr']);

  const ROLE_KIND: Record<Role, 'role-employee' | 'role-hr' | 'role-admin'> = {
    employee: 'role-employee',
    hr: 'role-hr',
    admin: 'role-admin'
  };

  const emptyCreateForm = (): HRCreateUserBody => ({
    name: '',
    email: '',
    role: 'employee',
    password: '',
    punch_mode: 'site'
  });

  let showCreate = $state(false);
  let createForm = $state<HRCreateUserBody>(emptyCreateForm());
  let createError = $state('');
  let creating = $state(false);

  function openCreate() {
    createForm = emptyCreateForm();
    createError = '';
    showCreate = true;
  }

  async function createUser() {
    if (!createForm.name.trim() || !createForm.email.trim()) {
      createError = 'Name and email are required.';
      return;
    }
    if (createForm.password.length < 8) {
      createError = 'Password must be at least 8 characters.';
      return;
    }
    creating = true;
    createError = '';
    try {
      await hrUsersApi.create(createForm);
      refresh();
      showCreate = false;
      toast.success('User created.');
    } catch (err) {
      createError = getErrorMessage(err);
    } finally {
      creating = false;
    }
  }

  let editing = $state<HRUser | null>(null);
  let editForm = $state<HRUpdateUserBody>({});
  let editError = $state('');
  let saving = $state(false);

  function openEdit(u: HRUser) {
    editing = u;
    editForm = {
      name: u.name,
      email: u.email,
      role: u.role,
      punch_mode: u.punch_mode ?? 'site'
    };
    editError = '';
  }

  async function saveEdit() {
    if (!editing) return;
    saving = true;
    editError = '';
    try {
      await hrUsersApi.update(editing.id, editForm);
      refresh();
      editing = null;
      toast.success('User updated.');
    } catch (err) {
      editError = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function toggleActive(u: HRUser) {
    const verb = u.active ? 'deactivate' : 'reactivate';
    if (!confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${u.name || u.email}?`)) return;
    try {
      await hrUsersApi.update(u.id, { active: !u.active });
      refresh();
      toast.success(`User ${u.active ? 'deactivated' : 'reactivated'}.`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  let resettingPasswordFor = $state<HRUser | null>(null);
  let newPassword = $state('');
  let resetPasswordError = $state('');
  let resettingPassword = $state(false);

  function openResetPassword(u: HRUser) {
    resettingPasswordFor = u;
    newPassword = '';
    resetPasswordError = '';
  }

  async function submitResetPassword() {
    if (!resettingPasswordFor) return;
    if (newPassword.length < 8) {
      resetPasswordError = 'Password must be at least 8 characters.';
      return;
    }
    resettingPassword = true;
    resetPasswordError = '';
    try {
      await hrUsersApi.resetPassword(resettingPasswordFor.id, newPassword);
      toast.success('Password reset.');
      resettingPasswordFor = null;
    } catch (err) {
      resetPasswordError = getErrorMessage(err);
    } finally {
      resettingPassword = false;
    }
  }

  async function resetDevice(u: HRUser) {
    if (!confirm(`Reset the registered device for ${u.name || u.email}? They'll be able to bind a new device on their next login.`))
      return;
    try {
      await hrDevicesApi.reset(u.id);
      refresh();
      toast.success('Device reset.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function resetMFA(u: HRUser) {
    if (!confirm(`Reset two-factor authentication for ${u.name || u.email}? They'll set it up again at next login.`))
      return;
    try {
      await hrUsersApi.resetMFA(u.id);
      refresh();
      toast.success('Two-factor authentication reset.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function copyDetails(u: HRUser) {
    const ok = await copyText(formatPersonDetails(u));
    if (ok) toast.success('Copied name, email, and user ID.');
    else toast.error('Could not copy details.');
  }

  function actionsFor(u: HRUser) {
    const items: { label: string; onClick: () => void; danger?: boolean }[] = [
      { label: 'Copy details', onClick: () => copyDetails(u) },
      { label: 'Edit', onClick: () => openEdit(u) },
      { label: 'Reset password', onClick: () => openResetPassword(u) }
    ];
    if (u.device_registered) items.push({ label: 'Reset device', onClick: () => resetDevice(u) });
    if (session.isAdmin && u.mfa_set) items.push({ label: 'Reset MFA', onClick: () => resetMFA(u) });
    if (u.id !== session.me?.id) {
      items.push({ label: u.active ? 'Deactivate' : 'Reactivate', onClick: () => toggleActive(u), danger: u.active });
    }
    return items;
  }
</script>

<div class="tym-page">
  <Toolbar searchValue={q} searchPlaceholder="Search people..." onSearch={(v) => (q = v)}>
    {#snippet filters()}
      <div class="flex flex-wrap items-center gap-1.5">
        {#each STATUS_FILTERS as f (f.id)}
          <button
            type="button"
            class="tym-chip {statusFilter === f.id ? 'active' : ''}"
            aria-pressed={statusFilter === f.id}
            onclick={() => (statusFilter = f.id)}
          >
            {f.label}
            <span class="tym-chip-count">{statusCounts[f.id]}</span>
          </button>
        {/each}
      </div>
    {/snippet}
    {#snippet actions()}
      <button type="button" class="btn-primary" onclick={openCreate}>
        <Plus class="h-4 w-4" /> New user
      </button>
    {/snippet}
  </Toolbar>

  {#if query.isError}
    <div class="tym-error-state">Could not load users.</div>
  {:else}
    <DataTable
      dense
      mobileCards
      loading={query.isLoading}
      skeletonRows={8}
      columns={[
        ...(session.isAdmin ? [{ key: 'dot', label: '', width: '28px', cell: dot }] : []),
        { key: 'name', label: 'Name', cell: name },
        { key: 'email', label: 'Email', cell: email },
        { key: 'role', label: 'Role', cell: roleCell },
        { key: 'status', label: 'Status', cell: statusCell },
        { key: 'punch', label: 'Punch', cell: punchCell },
        { key: 'device', label: 'Device', cell: deviceCell },
        { key: 'mfa', label: 'MFA', cell: mfaCell },
        { key: 'last_active', label: 'Last active', cell: lastActiveCell },
        ...(session.isAdmin ? [{ key: 'talk_to_them_by', label: 'Talk to them by', cell: talkToThemBy }] : []),
        { key: 'actions', label: '', align: 'right' as const, cell: actionsCell }
      ]}
      rows={filteredUsers}
      rowKey={(u) => u.id}
      onRowClick={openDetail}
      emptyTitle={isFiltered ? 'Nobody matches this view' : 'No users yet'}
      emptyBody={isFiltered
        ? 'Try a different search, or switch back to Everyone.'
        : 'Create the first account to give someone access.'}
    />
  {/if}

  {#if showCreate}
    <Modal title="New user" onClose={() => (showCreate = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">Name</div>
          <input type="text" class="input" aria-label="Name" bind:value={createForm.name} />
        </div>
        <div>
          <div class="label">Email</div>
          <input type="email" class="input" aria-label="Email" bind:value={createForm.email} />
        </div>
        <div>
          <div class="label">Role</div>
          <select class="input" aria-label="Role" bind:value={createForm.role}>
            {#each roleOptions as r (r)}
              <option value={r}>{r}</option>
            {/each}
          </select>
        </div>
        <div>
          <div class="label">Password</div>
          <input type="password" class="input" aria-label="Password" bind:value={createForm.password} />
        </div>
        <div>
          <div class="label">Punch</div>
          <select class="input" aria-label="Punch" bind:value={createForm.punch_mode}>
            {#each PUNCH_MODES as m (m.value)}
              <option value={m.value}>{m.label} — {m.hint}</option>
            {/each}
          </select>
        </div>

        {#if createError}<div class="tym-error-state">{createError}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showCreate = false)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={creating} onclick={createUser}>
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}

  {#if editing}
    <Modal title={`Edit — ${editing.name || editing.email}`} onClose={() => (editing = null)}>
      <div class="space-y-4">
        <div>
          <div class="label">Name</div>
          <input type="text" class="input" aria-label="Name" bind:value={editForm.name} />
        </div>
        <div>
          <div class="label">Email</div>
          <input type="email" class="input" aria-label="Email" bind:value={editForm.email} />
        </div>
        <div>
          <div class="label">Role</div>
          <select
            class="input"
            aria-label="Role"
            bind:value={editForm.role}
            disabled={editing.id === session.me?.id}
          >
            {#each roleOptions as r (r)}
              <option value={r}>{r}</option>
            {/each}
          </select>
          {#if editing.id === session.me?.id}
            <p class="mt-1 text-[12px] text-dark-gray">You cannot change your own role.</p>
          {/if}
        </div>
        <div>
          <div class="label">Punch</div>
          <select class="input" aria-label="Punch" bind:value={editForm.punch_mode}>
            {#each PUNCH_MODES as m (m.value)}
              <option value={m.value}>{m.label} — {m.hint}</option>
            {/each}
          </select>
        </div>

        {#if editError}<div class="tym-error-state">{editError}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (editing = null)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={saveEdit}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}

  {#if resettingPasswordFor}
    <Modal title={`Reset password — ${resettingPasswordFor.name || resettingPasswordFor.email}`} onClose={() => (resettingPasswordFor = null)}>
      <div class="space-y-4">
        <div>
          <div class="label">New password</div>
          <input type="password" class="input" aria-label="New password" bind:value={newPassword} />
        </div>
        {#if resetPasswordError}<div class="tym-error-state">{resetPasswordError}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (resettingPasswordFor = null)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={resettingPassword} onclick={submitResetPassword}>
            {resettingPassword ? 'Resetting...' : 'Reset password'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
</div>

{#snippet dot(u: HRUser)}
  {@const p = continuityFor(u.id)}
  {#if p}
    <span title={p.status_reason}><StatusLamp tone={LAMP_TONE[p.colour]} label={p.status_reason} /></span>
  {/if}
{/snippet}
{#snippet name(u: HRUser)}
  <span class="font-medium text-navy">{u.name || '—'}</span>
{/snippet}
{#snippet email(u: HRUser)}
  <span class="muted">{u.email}</span>
{/snippet}
{#snippet roleCell(u: HRUser)}
  <StatusBadge kind={ROLE_KIND[u.role]} label={u.role} />
{/snippet}
{#snippet statusCell(u: HRUser)}
  <span class="inline-flex items-center gap-1.5 {u.active ? 'text-navy' : 'text-dark-gray'}">
    <StatusLamp tone={u.active ? 'good' : 'idle'} />
    {u.active ? 'Active' : 'Inactive'}
  </span>
{/snippet}
{#snippet punchCell(u: HRUser)}
  {#if u.punch_mode && u.punch_mode !== 'site'}
    <span class="font-medium text-navy">{PUNCH_LABEL[u.punch_mode]}</span>
  {:else}
    <span class="muted">On site</span>
  {/if}
{/snippet}
{#snippet deviceCell(u: HRUser)}
  {#if u.device_registered}
    <StatusBadge kind="info" label="Registered" />
  {:else}
    <span class="muted">—</span>
  {/if}
{/snippet}
{#snippet mfaCell(u: HRUser)}
  {#if u.mfa_set}
    <StatusBadge kind="info" label="Set" />
  {:else}
    <span class="muted">—</span>
  {/if}
{/snippet}
{#snippet lastActiveCell(u: HRUser)}
  {#if u.last_active_at}
    <span class="tym-num muted whitespace-nowrap" title={new Date(u.last_active_at).toLocaleString()}>
      {relativeTime(u.last_active_at)}
    </span>
  {:else}
    <span class="muted">—</span>
  {/if}
{/snippet}
{#snippet talkToThemBy(u: HRUser)}
  {@const p = continuityFor(u.id)}
  <span class="muted">{p?.talk_to_them_by || '—'}</span>
{/snippet}
{#snippet actionsCell(u: HRUser)}
  <div onclick={(e) => e.stopPropagation()} role="presentation">
    <RowActions items={actionsFor(u)} />
  </div>
{/snippet}
